/**
 * Copy for the "connect a training app" doors (add-training hub + race wizard
 * Method step).
 *
 * Provider-agnostic on purpose: the server registers any number of connectors
 * and the manifest decides what's on offer, so the cards name the one provider
 * when there's one and speak generically when there are several. Pure string
 * helpers — no React, no manifest fetch — so both surfaces stay in step.
 */

/** Provider labels joined for prose: "A", "A or B", "A, B or C" (no Oxford). */
export function connectorLabelList(labels: readonly string[]): string {
  const named = labels.map((l) => l.trim()).filter(Boolean);
  if (named.length === 0) return "";
  if (named.length === 1) return named[0]!;
  return `${named.slice(0, -1).join(", ")} or ${named[named.length - 1]!}`;
}

/** Card title — names the provider when it's the only one on offer. */
export function connectorOfferTitle(labels: readonly string[]): string {
  const named = labels.map((l) => l.trim()).filter(Boolean);
  return named.length === 1 ? `Import from ${named[0]!}` : "Import from your training app";
}

/** Add-training hub description (still the connect-now surface). */
export function connectorOfferDesc(labels: readonly string[]): string {
  const named = labels.map((l) => l.trim()).filter(Boolean);
  const lead =
    named.length === 1
      ? `Connect your ${named[0]!} account`
      : `Connect ${connectorLabelList(named)}`;
  return `${lead} — we import upcoming planned workouts and keep them in sync. Add races yourself in Fuel Goods.`;
}

/**
 * Race-wizard Method step — nothing linked yet. OAuth stays after setup; this
 * card is a build-path choice that sets the expectation.
 */
export function connectorWizardDescAfterSetup(labels: readonly string[]): string {
  const named = labels.map((l) => l.trim()).filter(Boolean);
  if (named.length === 0) {
    return "You'll be able to import your workouts from your training app after setup.";
  }
  if (named.length === 1) {
    return `You'll be able to import your workouts from ${named[0]!} after setup.`;
  }
  return `You'll be able to import your workouts from ${connectorLabelList(named)} after setup.`;
}

/**
 * Wizard "Do you have a training plan?" step — already linked. Finishes like a
 * plain plan-less journey (no connect handoff), with copy that the connected
 * calendar is the training source.
 */
export function connectorWizardDescConnected(label: string): string {
  const name = label.trim() || "your training app";
  return `Your ${name} workouts will sync onto your calendar`;
}

/** Manage-connection modal note under the sync-workouts blurb. */
export function connectorEventsRacesSkipNote(providerId: string): string {
  const id = providerId.trim().toLowerCase();
  if (id === "trainingpeaks") {
    return "We don't import your events, please set them up manually here in Fuel Goods. Your rest days are not imported.";
  }
  if (id === "vdot" || id === "endorphins") {
    return "We don't import your races, please set them up manually here in Fuel Goods. Your rest days are not imported.";
  }
  return "We don't import your events/races, please set them up manually here in Fuel Goods. Your rest days are not imported.";
}

/** Auth-intro sentence: what we don't import from this provider. */
export function connectorEventsRacesIntroClause(providerId: string, label: string): string {
  const id = providerId.trim().toLowerCase();
  const name = label.trim() || "your training app";
  if (id === "vdot" || id === "endorphins") {
    return `Races aren't imported from ${name} — add those yourself in Fuel Goods. Your rest days are not imported.`;
  }
  if (id === "trainingpeaks") {
    return `Events aren't imported from ${name} — add those yourself in Fuel Goods. Your rest days are not imported.`;
  }
  return `Events/races aren't imported from ${name} — add those yourself in Fuel Goods. Your rest days are not imported.`;
}
